import { FC } from "react";
import "./KeyPeople.scss";
import {
  CoreFieldValue,
  LoadingV2,
  PrimaryButton,
  SectionPlaceholder,
} from "@ucl/library";
import { faCheckCircle, faPlus, faTimesCircle, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useKeyPeople } from "@/hooks/useKeyPeople";
import { KeyPersonItem } from "./KeyPersonItem";
import { KeyPersonTableRow } from "./KeyPersonTableRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { MessageComponent } from "@syncfusion/ej2-react-notifications";
import { useFinancialApplicationCoreData } from "@/hooks/useFinancialApplicationCoreData";
import { FinancialApplicationFormValidationDisplayBehavior, FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";

export interface KeyPeopleProps {
  financialApplicationId: string;
  onBlur?: (field: CoreFieldValue) => void;
  initiallyCollapseKeyPersonForms?: boolean;
  readOnly?: boolean;
  renderCollapsedAsTable?: boolean;
  validationBehavior?: FinancialApplicationFormValidationDisplayBehavior;
  validationContextType: FinancialApplicationFormSectionContextType;
  shouldHideSecurityQuestions?: boolean;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const KeyPeople: FC<KeyPeopleProps> = (props) => {
  const {
    keyPersonIds,
    handleDeleteKeyPerson,
    handleAddKeyPerson,
    isLoading,
    isCreating,
    metrics,
    handleApplicableRolesChange,
    validationErrors,
  } = useKeyPeople(props.financialApplicationId, props.validationBehavior, props.validationContextType);

  const { conditionalFlags } = useFinancialApplicationCoreData(props.financialApplicationId);

  return (
    <div className="key-people-info-people">
      {!props.readOnly && (
        <div className="key-people-info-people__header-info-validations">
          {Object.entries(validationErrors.fieldSchemaCollectionLevelValidationErrors).map(([key, value]) => (
            <MessageComponent title={key} severity="Error">
              {value.join(", ")}
            </MessageComponent>
          ))}
        </div>
      )}
      {!props.readOnly && (
      <div className="key-people-info-people__header">
        <ul className="key-people-info-people__header-info">
          <li>
            Beneficial Owner{" "}
            <span className={classNames({ active: (metrics?.beneficialOwnerCount || 0) > 0 })}>
              <FontAwesomeIcon icon={faUsers} /> {metrics?.beneficialOwnerCount}
            </span>
          </li>
          <li>
            Control Person{" "}
            <span className={classNames({ active: (metrics?.controlPersonCount || 0) === 1 })}>
              <FontAwesomeIcon
                icon={(metrics?.controlPersonCount || 0) === 1 ? faCheckCircle : faTimesCircle}
              />
            </span>
          </li>
          <li>
            Authorized Signer{" "}
            <span className={classNames({ active: (metrics?.authorizedSignerCount || 0) > 0 })}>
              <FontAwesomeIcon icon={faUsers} /> {metrics?.authorizedSignerCount}
            </span>
          </li>
        </ul>
        <PrimaryButton
          text="Add Key Person"
          iconProps={{ icon: faPlus }}
          onClick={handleAddKeyPerson}
          isLoading={isCreating}
          loadingProgressContent="Add Key Person"
          loadingSuccessContent="Add Key Person"
        />
        </div>
      )}
      <div className="key-people-info-people__content">
          {isLoading && <LoadingV2 />}
          {!props.readOnly && !isLoading && keyPersonIds.length === 0 && (
            <SectionPlaceholder label="No key people. Add one to get started." />
          )}
          {props.readOnly && !isLoading && keyPersonIds.length === 0 && (
            <div className="empty-state-message">
              <p>No key people have been added.</p>
            </div>
          )}
          {!isLoading && props.renderCollapsedAsTable && keyPersonIds.length > 0 && (
            <div className="key-people-table">
              <table className="key-people-table__table">
                <thead className="key-people-table__header">
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Date of Birth</th>
                    <th>SSN</th>
                    <th>Address</th>
                    {conditionalFlags.shouldShowAttorneyBarNumber && (
                      <th>Attorney Bar Number</th>
                    )}
                  </tr>
                </thead>
                <tbody className="key-people-table__body">
                  {keyPersonIds.map((id) => (
                    <KeyPersonTableRow 
                      key={id}
                      financialApplicationId={props.financialApplicationId}
                      keyPersonId={id}
                      onDelete={handleDeleteKeyPerson}
                      validationErrors={validationErrors.fieldSchemaCollectionMemberValidationErrors[id] || {}}
                      readOnly={props.readOnly}
                      showAttorneyBarNumber={conditionalFlags.shouldShowAttorneyBarNumber}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!isLoading && !props.renderCollapsedAsTable && keyPersonIds.map((id) => (
              <KeyPersonItem
                key={id}
                financialApplicationId={props.financialApplicationId}
                keyPersonId={id}
                onDelete={handleDeleteKeyPerson}
                validationErrors={validationErrors.fieldSchemaCollectionMemberValidationErrors[id] || {}}
                onApplicableRolesChange={handleApplicableRolesChange}
                onBlur={props.onBlur}
                isInitiallyCollapsed={props.initiallyCollapseKeyPersonForms}
                allowChangingWhetherCollapsed={!props.readOnly}
                alreadyHasControlPersonRole={(metrics?.controlPersonCount ?? 0) > 0}
                validationContextType={props.validationContextType}
                shouldHideSecurityQuestions={props.shouldHideSecurityQuestions}
                shouldAskIfCanProvideGovernmentIdInformation={props.shouldAskIfCanProvideGovernmentIdInformation}
                shouldAlwaysShowGovernmentIdInformationFields={props.shouldAlwaysShowGovernmentIdInformationFields}
                shouldShowDigitalBankingFields={props.shouldShowDigitalBankingFields}
                shouldShowDebitCardFields={props.shouldShowDebitCardFields}
              />
            ))}
      </div>
    </div>
  );
};
